import { SelectProps, Spin } from 'antd';
import { Select } from 'components';
import {
  GetAmenitiesParams,
  useGetAmenitiesInfiniteQuery,
} from 'features/amenities';
import { usePaginationParams } from 'hooks';
import debounce from 'lodash/debounce';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Amenity } from 'types/amenity';

const initialParams = {
  page: 1,
  size: 10,
};

type AmenitiesSelectProps = {
  customParams?: GetAmenitiesParams;
};

export const AmenitiesSelect = ({
  customParams,
  ...props
}: SelectProps & AmenitiesSelectProps) => {
  const { t } = useTranslation();

  const { params, setParams } =
    usePaginationParams<GetAmenitiesParams>(initialParams);

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAmenitiesInfiniteQuery(
      { ...params, ...customParams },
      {
        keepPreviousData: true,
        cacheTime: 2000,
      },
    );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce(
      (amenityName?: string) =>
        setParams((prevParams) => ({
          ...prevParams,
          ...{ name: amenityName },
        })),
      500,
    ),
    [],
  );

  const handleClear = () => setParams(initialParams);

  return (
    <Select<Amenity>
      placeholder={t('amenities.formFields.placeholder')}
      onSearch={debouncedOnSearch}
      onClear={handleClear}
      loading={isFetching}
      dropdownMatchSelectWidth={200}
      notFoundContent={
        isFetching ? (
          <Spin size='small' aria-label={t('amenities.formFields.loading')} />
        ) : null
      }
      options={data?.pages
        .map((page) =>
          page.items.map((amenityItem) => ({
            value: amenityItem.id,
            label: amenityItem.name,
          })),
        )
        .flat()}
      onPopupScroll={(e) => {
        if (!e.target) return;

        const target = e.target as HTMLDivElement;
        const isScrolledToBottom =
          target.scrollTop + target.offsetHeight === target.scrollHeight;

        if (!isScrolledToBottom) return;

        if (hasNextPage || !isFetchingNextPage) {
          fetchNextPage();
        }
      }}
      allowClear
      {...props}
    />
  );
};
